import { Button, ButtonLevel, ButtonSize, Tooltip, TooltipTrigger } from '@vizit/atoms';
import * as React from 'react';
import type { ProductListingData } from './product-listing-row';

/**
 * Action configuration for row action buttons
 */
export interface RowAction {
  /**
   * Unique identifier for the action
   */
  id: string;
  /**
   * Icon to display in the button
   */
  icon: React.ReactNode;
  /**
   * Accessible label for the action
   */
  label: string;
  /**
   * Callback fired when the action is triggered
   */
  onAction: (item: ProductListingData) => void;
  /**
   * Optional tooltip text
   */
  tooltip?: string | ((item: ProductListingData) => string);
  /**
   * Whether the action is disabled
   */
  isDisabled?: boolean | ((item: ProductListingData) => boolean);
  /**
   * Whether the action is hidden
   */
  isHidden?: boolean | ((item: ProductListingData) => boolean);
  /**
   * Button level (defaults to Ghost)
   */
  level?: ButtonLevel;
  /**
   * Button size (defaults to Sm)
   */
  size?: ButtonSize;
}

interface RowActionButtonsProps {
  actions: RowAction[];
  item: ProductListingData;
}

/**
 * Memoized action buttons component for optimal performance
 */
const RowActionButtons = React.memo<RowActionButtonsProps>(({ actions, item }) => {
  const { productName } = item;

  const visibleActions = actions.filter((action) => {
    const isHidden = typeof action.isHidden === 'function' ? action.isHidden(item) : action.isHidden;
    return !isHidden;
  });

  return (
    <>
      {visibleActions.map((action) => {
        const button = (
          <Button
            key={action.id}
            level={action.level ?? ButtonLevel.Ghost}
            size={action.size ?? ButtonSize.Sm}
            onPress={() => {
              action.onAction(item);
            }}
            isDisabled={typeof action.isDisabled === 'function' ? action.isDisabled(item) : action.isDisabled}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            aria-label={`${action.label} ${productName}`}
            data-testid={`library-products-row-${action.id}`}
            iconStart={action.icon}
          />
        );

        if (action.tooltip) {
          return (
            <TooltipTrigger key={action.id}>
              {button}
              <Tooltip>{typeof action.tooltip === 'function' ? action.tooltip(item) : action.tooltip}</Tooltip>
            </TooltipTrigger>
          );
        }

        return button;
      })}
    </>
  );
});

RowActionButtons.displayName = 'RowActionButtons';

export { RowActionButtons };
